import React from 'react';
import { Eye, ChefHat, Utensils } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchKitchenDiningSurveyDetail } from '../../redux/actions/kitchenDiningSurveyActions';
import { useLanguage } from "../../context/LanguageContext";

const KitchenDiningSurveyList = ({
  canteen,
  surveys,
  loading,
  onViewSurveyDetail,
  onBack,
  selectedSurvey
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  if (!canteen) return null;

  // Handle completing/resuming a survey
  const handleCompleteSurvey = async (survey) => {
    try {
      console.log("Fetching kitchen dining survey detail for survey ID:", survey.id);
      
      // Fetch the complete survey detail
      const response = await dispatch(fetchKitchenDiningSurveyDetail(survey.id));
      
      // Extract API response safely
      const surveyDetail = response?.payload?.data || response?.data || response;
      
      console.log("✅ Kitchen Dining Survey Detail loaded:", surveyDetail);
      
      if (!surveyDetail) {
        throw new Error("No survey data received");
      }
      
      // Save to localStorage for persistence
      localStorage.setItem("selectedSurvey", JSON.stringify(surveyDetail));
      localStorage.setItem("selectedCanteen", JSON.stringify(canteen));
      localStorage.setItem("surveyType", "kitchendining");
      localStorage.setItem("surveyId", survey.id);
      
      // Navigate with full data
      navigate("/survey/sections", {
        state: {
          canteen: canteen,
          surveyId: survey.id,
          surveyDetail: surveyDetail,
          resume: true,
          surveyType: "kitchendining"
        }
      });
      
    } catch (error) {
      console.error("❌ Error fetching kitchen dining survey detail:", error);
      alert(t.global.alert_server_error || "Failed to load survey. Please try again.");
    }
  };

  // Handle viewing completed survey
  const handleViewSurvey = async (survey) => {
    try {
      console.log("Viewing completed kitchen dining survey:", survey.id);
      
      const response = await dispatch(fetchKitchenDiningSurveyDetail(survey.id));
      const surveyDetail = response?.payload?.data || response?.data || response;
      
      if (onViewSurveyDetail) {
        onViewSurveyDetail({ ...survey, detail: surveyDetail });
      }
      
    } catch (error) {
      console.error("Error fetching kitchen dining survey detail:", error);
      alert(t.global.alert_server_error || "Failed to load survey details. Please try again.");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t.global.unknown_date || 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="mt-8">
      {/* Header */}
      <div className="flex items-center mb-4">
        <ChefHat className="text-orange-600 mr-2" />
        <Utensils className="text-orange-500 mr-2" />
        <h3 className="text-xl font-bold text-gray-800">
          {t.global.kitchen_cum_dining_surveys_title?.replace("{areaName}", canteen.area_name || canteen.kitchen_dining_location_fulladdress || 'Kitchen Cum Dining') || `Kitchen Cum Dining Surveys – ${canteen.area_name || canteen.kitchen_dining_location_fulladdress || 'Kitchen Cum Dining'}`}
        </h3>
      </div>

      {/* Loader */}
      {loading ? (
        <div className="text-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mx-auto"></div>
        </div>
      ) : surveys?.length === 0 ? (
        <div className="text-center p-6 border border-dashed border-gray-300 rounded-lg">
          <div className="flex justify-center mb-2">
            <ChefHat className="text-gray-400 mr-2" size={40} />
            <Utensils className="text-gray-400" size={40} />
          </div>
          <p className="text-gray-500 text-lg">
            {t.global.no_kitchen_cum_dining_surveys_found || "No kitchen cum dining surveys found for this canteen"}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {t.global.start_new_survey || "Start a new survey to begin data collection"}
          </p>
        </div>
      ) : (
        <>
          {/* Warning for incomplete surveys */}
          {surveys.some(s => !s.total_survey_completed) && (
            <div className="mb-3 p-2 bg-orange-100 text-orange-700 text-sm rounded">
              {t.global.incomplete_surveys_warning || "⚠ Some surveys are incomplete. Click \"Resume Survey\" to continue."}
            </div>
          )}

          {/* Survey List */}
          <div className="space-y-3">
            {surveys.map((survey) => {
              const isCompleted = survey.total_survey_completed === true || survey.total_survey_completed === 1;
              
              return (
                <div
                  key={survey.id}
                  className={`border p-4 rounded flex justify-between items-center transition ${
                    isCompleted
                      ? "border-orange-200 hover:bg-orange-50"
                      : "bg-orange-50 border-orange-200"
                  }`}
                >
                  {/* Left Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {t.global.kitchen_cum_dining_survey_id?.replace("{id}", survey.id) || `Kitchen Cum Dining Survey #${survey.id}`}
                      </p>
                      {!isCompleted && (
                        <span className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                          {t.global.survey_incomplete || "Incomplete"}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-500 mt-1">
                      {t.global.survey_created?.replace("{date}", formatDate(survey.created_at)) || `Created: ${formatDate(survey.created_at)}`}
                    </p>

                    <div className="mt-2">
                      <span
                        className={`text-xs inline-block px-2 py-1 rounded-full ${
                          isCompleted
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {isCompleted 
                          ? t.global.status_completed_badge || "✓ Completed" 
                          : t.global.status_pending_badge || "⏳ Pending"}
                      </span>
                    </div>

                    {!isCompleted && (
                      <div className="text-xs text-orange-600 mt-1">
                        {t.global.action_required || "⚡ Action Required - Please complete this survey"}
                      </div>
                    )}
                  </div>

                  {/* Right Button */}
                  {isCompleted ? (
                    <button
                      onClick={() => handleViewSurvey(survey)}
                      className="px-4 py-2 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition flex items-center gap-2"
                    >
                      <Eye size={16} />
                      {t.global.view_details || "View Details"}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleCompleteSurvey(survey)}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded flex items-center gap-2 transition"
                    >
                      <span>📝</span>
                      {t.global.resume_survey || "Resume Survey"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Back Button */}
      {selectedSurvey && (
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
        >
          {t.global.back_to_survey_list || "← Back to Survey List"}
        </button>
      )}
    </div>
  );
};

export default KitchenDiningSurveyList;